import { Component } from '@angular/core';
import _ from 'lodash';
import { Chart, registerables } from 'chart.js';
import moment from 'moment';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { RTLDivDirectiveDirective } from '../../../directives/rtldiv-directive.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { TranslatorService } from '../../../services/translator_service';
import { ResponsiveService } from '../../../services/responsive.service';
import { ApiQuery } from '../../../web-services/api/api.query';

Chart.register(...registerables);

@Component({
  selector: 'app-system-recommendation',
  imports: [TranslatePipe, RTLDivDirectiveDirective],
  templateUrl: './system-recommendation.html',
  styleUrl: './system-recommendation.scss',
})
export class SystemRecommendation {
  isMobile: boolean;
  translationsObj;
  currentLang;
  spinnerActive:boolean = true;
  chart: Chart = null;
  selectedRange = 'day';
  rawData: any[] = [];
  totalRecommendedCollections: any[] = [];
  navigationLabel = '';
  currentPage = 0;
  pageSize = 7;
  private readonly destroy$ = new Subject<void>();
  direction: string;
  fullChartData = {
    labels: [],
    nonRecommendedData: [],
    systemRecommendedData: [],
    systemRecommendedExtraData: [],
    labelDates: [] 
  };
  private bucketItems: any[][] = [];

  constructor(
    private translator: TranslatorService,
    private responsiveService: ResponsiveService,
    private apiQuery: ApiQuery
  ) {
    this.translator.currentLangEmitter$.subscribe(async (value) => {
      this.translationsObj = await this.translator
        .getTranslation(value)
        .toPromise();
      this.currentLang = value;
      this.direction = value == 'iw' ? 'rtl' : 'ltr';
    });
  }

  ngOnInit() {
    this.onResize();
    this.responsiveService.checkWidth();
    combineLatest([
      this.apiQuery.collectionsStatsInCollections$,
      this.apiQuery.dailyWorkPlansTotalRecommendedCollections$,
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([data, dailyWorkPlansTotalRecommendedCollections]) => {
        this.spinnerActive = true;
        if (data.length == 0) return;
        this.spinnerActive = false;
        this.rawData = [];
        this.totalRecommendedCollections = dailyWorkPlansTotalRecommendedCollections;
        this.rawData = data.flatMap(site =>
          Object.values(site["site_bins_collections"] ?? {}).flatMap((range: any[]) => range)
        );
        this.currentPage = 0;
        this.applyMomentLocale(String(this.currentLang));
        this.rebuildChartData();
        this.initChart();
      });
  }

  onResize() {
    this.responsiveService.getMobileStatus().subscribe((isMobile) => {
      this.isMobile = isMobile;
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  changeRange(range: string) {
    this.selectedRange = range;
    this.currentPage = 0;
    this.rebuildChartData();
    this.updateChart();
  }

  prevPage() {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.updateChart();
    }
  }

  nextPage() {
    const maxPage = Math.ceil(this.fullChartData.labels.length / this.pageSize) - 1;

    if (this.currentPage < maxPage) {
      this.currentPage++;
      this.updateChart();
    }
  }

  updateChart() {
    const visible = this.getVisibleChartData();

    this.chart.data.labels = visible.labels;
    this.chart.data.datasets[0].data = visible.nonRecommendedData;
    this.chart.data.datasets[1].data = visible.systemRecommendedData;
    this.chart.data.datasets[2].data = visible.systemRecommendedExtraData;
    (this.chart.options as any).scales.y.suggestedMax = this.computeYAxisMax();
    this.chart.update('none');

    this.updateNavigationLabel();
  }

  private computeYAxisMax(): number {
    let max = 0;
    const len = this.fullChartData.nonRecommendedData.length;
    for (let i = 0; i < len; i++) {
      const recommended = (this.fullChartData.systemRecommendedData[i] || 0)
        + (this.fullChartData.systemRecommendedExtraData[i] || 0);
      const nonRecommended = this.fullChartData.nonRecommendedData[i] || 0;
      max = Math.max(max, recommended, nonRecommended);
    }
    return max * 1.5;
  }

  getVisibleChartData() {
    const start = this.currentPage * this.pageSize;
    const end = start + this.pageSize;

    return {
      labels: this.fullChartData.labels.slice(start, end),
      nonRecommendedData: this.fullChartData.nonRecommendedData.slice(start, end),
      systemRecommendedData: this.fullChartData.systemRecommendedData.slice(start, end),
      systemRecommendedExtraData: this.fullChartData.systemRecommendedExtraData.slice(start, end),
      labelDates: this.fullChartData.labelDates.slice(start, end)
    };
  }

  updateNavigationLabel() {
    const visible = this.getVisibleChartData();
    if (!visible.labelDates.length) {
      this.navigationLabel = '';
      return;
    }

    const first = visible.labelDates[0];
    const last = visible.labelDates[visible.labelDates.length - 1];

    if (this.selectedRange == 'day') {
      this.navigationLabel =
        first.start.format('DD/MM/YY') + ' - ' + last.start.format('DD/MM/YY');
      return;
    }

    if (this.selectedRange == 'week') {
      this.navigationLabel =
        first.start.format('DD/MM/YY') + ' - ' + last.end.format('DD/MM/YY');
      return;
    }

    if (this.selectedRange == 'month') {
      this.navigationLabel =
        first.start.format('MMM YYYY') + ' - ' + last.start.format('MMM YYYY');
    }
  }

  rebuildChartData() {
    if (this.selectedRange == 'day') {
      this.fullChartData = this.buildDayChartData();
    } else if (this.selectedRange == 'week') {
      this.fullChartData = this.buildWeekChartData();
    } else if (this.selectedRange == 'month') {
      this.fullChartData = this.buildMonthChartData();
    }

    const byDay = this.buildItemsByDayKey();
    this.bucketItems = this.fullChartData.labelDates.map(({ start, end }) =>
      this.collectItemsForDayRange(byDay, start, end)
    );
  }

  /** Single pass over rawData; same day key as moment(..., 'YYYY-MM-DD HH:mm:ss').startOf('day').format('YYYY-MM-DD'). */
  private buildItemsByDayKey(): Map<string, any[]> {
    const byDay = new Map<string, any[]>();
    for (const item of this.rawData) {
      if (!item.bin_collection_time) continue;
      const itemDate = moment(item.bin_collection_time, 'YYYY-MM-DD HH:mm:ss').startOf('day');
      const key = itemDate.format('YYYY-MM-DD');
      let arr = byDay.get(key);
      if (!arr) {
        arr = [];
        byDay.set(key, arr);
      }
      arr.push(item);
    }
    return byDay;
  }

  private collectItemsForDayRange(
    byDay: Map<string, any[]>,
    start: moment.Moment,
    end: moment.Moment
  ): any[] {
    const startDay = start.clone().startOf('day');
    const endDay = end.clone().startOf('day');
    if (startDay.isSame(endDay, 'day')) {
      return byDay.get(startDay.format('YYYY-MM-DD')) ?? [];
    }
    const out: any[] = [];
    for (let d = startDay.clone(); d.isSameOrBefore(endDay, 'day'); d.add(1, 'day')) {
      const arr = byDay.get(d.format('YYYY-MM-DD'));
      if (arr) {
        for (const x of arr) {
          out.push(x);
        }
      }
    }
    return out;
  }

  buildDayChartData() {
    const grouped = {};

    this.rawData
      .filter(item => item.bin_collection_time)
      .forEach(item => {
        const date = moment(item.bin_collection_time, 'YYYY-MM-DD HH:mm:ss').startOf('day');
        const key = date.format('YYYY-MM-DD');

        if (!grouped[key]) {
          grouped[key] = {
            label: date.format('DD.MM.YYYY'),
            start: moment(date),
            end: moment(date),
            nonRecommended: 0,
            systemRecommended: 0
          };
        }

        if (item.is_part_of_work_plan) {
          grouped[key].systemRecommended ++;
        } else if (!item.is_part_of_work_plan) {
          grouped[key].nonRecommended ++;
        }
      });

    const sorted = Object.keys(grouped)
      .map(key => grouped[key])
      .sort((a, b) => a.start.valueOf() - b.start.valueOf());

    return {
      labels: sorted.map(x => x.label),
      nonRecommendedData: sorted.map(x => x.nonRecommended),
      systemRecommendedData: sorted.map(x => x.systemRecommended),
      systemRecommendedExtraData: sorted.map(x => {
        const total = this.getTotalRecommendedForDateRange(x.start, x.end);
        const extra = total - x.systemRecommended;
        return extra > 0 ? extra : 0;
      }),
      labelDates: sorted.map(x => ({ start: x.start, end: x.end }))
    };
  }

  buildWeekChartData() {
    const validItems = this.rawData
      .filter(item => item.bin_collection_time)
      .map(item => ({
        ...item,
        date: moment(item.bin_collection_time, 'YYYY-MM-DD HH:mm:ss').startOf('day')
      }))
      .sort((a, b) => a.date.valueOf() - b.date.valueOf());

    const result = [];
    let i = 0;

    while (i < validItems.length) {
      const start = moment(validItems[i].date);
      const maxEnd = moment(start).add(6, 'days');

      let end = moment(validItems[i].date);
      let nonRecommended = 0;
      let systemRecommended = 0;
      let j = i;

      while (j < validItems.length && validItems[j].date.isSameOrBefore(maxEnd, 'day')) {
        end = moment(validItems[j].date);

        if (validItems[j].is_part_of_work_plan) {
          systemRecommended ++;
        } else if (!validItems[j].is_part_of_work_plan) {
          nonRecommended ++;
        }

        j++;
      }

      result.push({
        label: start.isSame(end, 'day')
          ? start.format('DD.MM.YYYY')
          : '\u202A' + start.format('DD.MM.YYYY') + ' - ' + end.format('DD.MM.YYYY') + '\u202C',
        start: moment(start),
        end: moment(end),
        nonRecommended,
        systemRecommended
      });

      i = j;
    }

    return {
      labels: result.map(x => x.label),
      nonRecommendedData: result.map(x => x.nonRecommended),
      systemRecommendedData: result.map(x => x.systemRecommended),
      systemRecommendedExtraData: result.map(x => {
        const total = this.getTotalRecommendedForDateRange(x.start, x.end);
        const extra = total - x.systemRecommended;
        return extra > 0 ? extra : 0;
      }),
      labelDates: result.map(x => ({ start: x.start, end: x.end }))
    };
  }

  buildMonthChartData() {
    const grouped = {};

    this.rawData
      .filter(item => item.bin_collection_time)
      .forEach(item => {
        const date = moment(item.bin_collection_time, 'YYYY-MM-DD HH:mm:ss');
        const key = date.format('YYYY-MM');

        if (!grouped[key]) {
          grouped[key] = {
            label: date.format('MMM'),
            start: moment(date).startOf('month'),
            end: moment(date).endOf('month'),
            nonRecommended: 0,
            systemRecommended: 0
          };
        }

        if (item.is_part_of_work_plan) {
          grouped[key].systemRecommended ++;
        } else if (!item.is_part_of_work_plan) {
          grouped[key].nonRecommended ++;
        }
      });

    const sorted = Object.keys(grouped)
      .map(key => grouped[key])
      .sort((a, b) => a.start.valueOf() - b.start.valueOf());

    return {
      labels: sorted.map(x => x.label),
      nonRecommendedData: sorted.map(x => x.nonRecommended),
      systemRecommendedData: sorted.map(x => x.systemRecommended),
      systemRecommendedExtraData: sorted.map(x => {
        const total = this.getTotalRecommendedForDateRange(x.start, x.end);
        const extra = total - x.systemRecommended;
        return extra > 0 ? extra : 0;
      }),
      labelDates: sorted.map(x => ({ start: x.start, end: x.end }))
    };
  }

  initChart = () => {   
    if (this.chart) {
			this.chart.destroy();
		} 
    const visible = this.getVisibleChartData();
		let canvas: any = document.getElementById("systemRecommendationData");
    if(!canvas){
      return;
    }
    let ctx = canvas.getContext('2d');
    let dataArr = {
      labels: visible.labels,
      datasets: [
        {
          label: this.translationsObj.TITLES.NON_RECOMMENDED_COLLECTIONS,
          data: visible.nonRecommendedData,
          backgroundColor: '#485760',        
          barThickness: 24,
          borderRadius: 4,
          stack: 'nonRecommended'
        },
        {
          label: this.translationsObj.TITLES.SYSTEM_RECOMMENDED_COLLECTIONS,
          data: visible.systemRecommendedData,
          backgroundColor: '#64A5F2',        
          barThickness: 24,
          borderRadius: 4,
          stack: 'recommended'
        },
        {
          label: this.translationsObj.TITLES.REMAINING_RECOMMENDED_COLLECTIONS,
          data: visible.systemRecommendedExtraData,
          backgroundColor: '#64A5F233',
          barThickness: 24,
          borderRadius: 4,
          stack: 'recommended'
        }
      ]
    };
    
    this.chart = new Chart(ctx, {
      type: "bar",
      data: dataArr,
      plugins: [{
        id: 'stackGapShift',
        afterUpdate: (chart) => {
          const gap = 1;
          chart.config.data.datasets.forEach((dataset, i) => {
            const meta = chart.getDatasetMeta(i);
            meta.data.forEach((bar: any) => {
              if (i == 0) {
                bar.x -= gap;
              } else {
                bar.x += gap;
              }
            });
          });
        }
      }],
      options: {
        onClick: (event, elements) => {
          if (!elements || elements.length == 0) return;
          const element: any = elements[0];
          const datasetIndex = element.datasetIndex;
          const localIndex = element.index;
          const clickedItems = this.getItemsForBar(localIndex, datasetIndex);
        },
        hover: {
          mode: 'index',
          intersect: false
        },
        onHover: (_event, elements) => {
          const chart = this.chart;
          if (!chart) return;
          chart.canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';

          const hoveredIndex = elements.length > 0 ? (elements[0] as any).index : -1;

          chart.data.datasets.forEach((dataset, di) => {
            const meta = chart.getDatasetMeta(di);
            meta.data.forEach((bar: any, bi) => {
              const original = dataset.backgroundColor as string;
              const color = (hoveredIndex == -1 || bi == hoveredIndex)
                ? original
                : this.hexToRgba(original, 0.3);
              bar.options.backgroundColor = color;
            });
          });
          chart.render();
        },
        plugins: {
          legend: {
            position: 'bottom',
            align: 'start',
            onClick: () => {},
            labels: {
              color: '#2A3338',
              font: {
                size: 10,
                weight: 400
              },
              usePointStyle: true,
              pointStyle: 'circle',
              filter: (legendItem) => legendItem.datasetIndex !== 2
            }
          },
          tooltip: {
          enabled: false,
          intersect: false,
          mode: 'index',
          external: (context) => {
            const tooltip: any = context.tooltip;
            let tooltipEl = document.getElementById('chartjs-tooltip-recommendation');
            if (!tooltipEl) {
              tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip-recommendation';
              document.body.appendChild(tooltipEl);
            }
            if (tooltip.opacity == 0) {
              tooltipEl.style.opacity = '0';
              tooltipEl.style.transform = '';
              return;
            }
            tooltipEl.classList.remove('above', 'below', 'no-transform');
            if (tooltip.yAlign) {
              tooltipEl.classList.add(tooltip.yAlign);
            } else {
              tooltipEl.classList.add('no-transform');
            }
            if (tooltip.body && tooltip.dataPoints) {
              const localIndex = tooltip.dataPoints[0].dataIndex;
              const allItems = this.getItemsForBar(localIndex);
              const nonRecommended = allItems.filter(i => !i.is_part_of_work_plan);
              const systemRecommended = allItems.filter(i => i.is_part_of_work_plan);
              const total = allItems.length;

              const globalIdx = this.currentPage * this.pageSize + localIndex;
              const dateRange = this.fullChartData.labelDates[globalIdx];
              const totalRecommended = dateRange
                ? this.getTotalRecommendedForDateRange(dateRange.start, dateRange.end)
                : 0;
              const coverage = totalRecommended > 0
                ? Math.round((systemRecommended.length / totalRecommended) * 100)
                : 0;

              let innerHtml = `
                <div style="flex-direction:column;display:flex;gap:6px;padding:6px 6px;min-width:280px;">
                  <div style="justify-content: space-between;direction:${this.direction};border-radius:4px;padding:2px 2px;display:flex;align-items:center;font-size:12px;font-weight:400;font-family:Open Sans,sans-serif">
                    <span>${this.translationsObj.COLLECTION_REPORT.TOTAL_COLLECTIONS}</span>
                    <span>${total}</span>
                  </div>
                  <div style="justify-content: space-between;direction:${this.direction};border-radius:4px;padding:2px 2px;display:flex;align-items:center;font-size:12px;font-weight:400;font-family:Open Sans,sans-serif">    
                    <span>${this.translationsObj.COLLECTION_REPORT.TOTAL_NON_RECOMMENDED_COLLECTIONS}</span>
                    <span>${nonRecommended.length}</span>
                  </div>
                  <div style="justify-content: space-between;direction:${this.direction};border-radius:4px;padding:2px 2px;display:flex;align-items:center;font-size:12px;font-weight:400;font-family:Open Sans,sans-serif">
                    <span>${this.translationsObj.COLLECTION_REPORT.TOTAL_NON_WORKPLAN_COLLECTIONS}</span>
                    <span>${totalRecommended - systemRecommended.length}</span>
                  </div>
                  <div style="justify-content: space-between;direction:${this.direction};border-radius:4px;padding:2px 2px;display:flex;align-items:center;font-size:12px;font-weight:400;font-family:Open Sans,sans-serif">
                    <span>${this.translationsObj.COLLECTION_REPORT.TOTAL_SYSTEM_RECOMMENDED_COLLECTIONS}</span>
                    <span>${systemRecommended.length}</span>
                  </div>
                  <div style="width: 100%; height: 1px; background-color: #CDD2D4; margin: 6px 0;"></div>
                  <div style="justify-content: space-between;direction:${this.direction};border-radius:4px;padding:2px 2px;display:flex;align-items:center;font-size:12px;font-weight:400;font-family:Open Sans,sans-serif">
                    <span>${this.translationsObj.COLLECTION_REPORT.TOTAL_SYSTEM_COLLECTION_RECOMMENDATIONS}</span>
                    <span>${totalRecommended}</span>
                  </div>
                  <div style="justify-content: space-between;direction:${this.direction};border-radius:4px;padding:2px 2px;display:flex;align-items:center;font-size:12px;font-weight:400;font-family:Open Sans,sans-serif">
                    <span>${this.translationsObj.COLLECTION_REPORT.WORKPLAN_COLLECTIONS_COVERAGE}</span>
                    <span>${coverage}%</span>
                  </div>
                </div>`;
              tooltipEl.innerHTML = innerHtml;
            }
            const position = this.chart.canvas.getBoundingClientRect();

            let topY = tooltip.caretY;
            if (tooltip.dataPoints && tooltip.dataPoints.length > 0) {
              const localIndex = tooltip.dataPoints[0].dataIndex;
              let minY = Infinity;
              this.chart.data.datasets.forEach((_ds, di) => {
                const meta = this.chart.getDatasetMeta(di);
                const bar = meta.data[localIndex];
                if (bar && (bar as any).y < minY) {
                  minY = (bar as any).y;
                }
              });
              if (minY !== Infinity) topY = minY;
            }

            tooltipEl.style.opacity = '1';
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.color = 'white';
            tooltipEl.style.top = position.top + window.scrollY + topY + 'px';
            const tooltipLeft = position.left + window.scrollX + tooltip.caretX;
            const tooltipWidth = tooltipEl.offsetWidth || 220;
            const isRtl = this.currentLang == 'iw';

            tooltipEl.style.left = tooltipLeft + 'px';
            if (isRtl) {
              if (tooltipLeft - tooltipWidth < window.scrollX) {
                tooltipEl.style.transform = 'translateY(calc(-100% - 8px))';
              } else {
                tooltipEl.style.transform = 'translate(-100%, calc(-100% - 8px))';
              }
            } else {
              if (tooltipLeft + tooltipWidth > window.innerWidth + window.scrollX) {
                tooltipEl.style.transform = 'translate(-100%, calc(-100% - 8px))';
              } else {
                tooltipEl.style.transform = 'translateY(calc(-100% - 8px))';
              }
            }
            tooltipEl.style.fontFamily = 'Open Sans, sans-serif';
            tooltipEl.style.fontSize = '14px';
            tooltipEl.style.fontStyle = 'normal';
            tooltipEl.style.padding = '8px';
            tooltipEl.style.pointerEvents = 'none';
            tooltipEl.style.backgroundColor = '#2A3338';
            tooltipEl.style.boxShadow = '2px 6px 28px 0px rgba(42, 51, 56, 0.30)';
            tooltipEl.style.borderRadius = '8px';
            tooltipEl.style.zIndex = '9999';
            }
          }
        },
        animation: false,
        maintainAspectRatio: false,
      	scales: {
          y: {
            stacked: true,
            position: 'left',
            beginAtZero: true,
            suggestedMax: this.computeYAxisMax(),
            ticks: {
              font: {
                family: '"Open Sans", sans-serif',
                size: 12
              },
              color: '#9DA9AD'
            }
          },
          x: {
            stacked: true,
            display: true,
            grid: {
              display: false
            },
            ticks: {
              font: {
                family: '"Open Sans", sans-serif',
                size: 12
              },
              color: '#9DA9AD'
            }
          }
        }
      }
    });
    this.updateNavigationLabel();
  }

  private mapAppLangToMomentLocale(lang: string): string {
    switch (lang) {
      case 'iw':
        return 'he';
      case 'pl':
        return 'pl';
      case 'en':
        return 'en';
      default:
        return 'en';
    }
  }

  private applyMomentLocale(lang: string) {
    moment.locale(this.mapAppLangToMomentLocale(lang));
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private getTotalRecommendedForDateRange(start: moment.Moment, end: moment.Moment): number {
    let total = 0;
    for (const entry of this.totalRecommendedCollections) {
      const planDate = moment(entry.plan_date, 'YYYY-MM-DD');
      if (planDate.isSameOrAfter(start, 'day') && planDate.isSameOrBefore(end, 'day')) {
        for (const site of (entry.sites_info || [])) {
          total += site.recommended_collections_count || 0;
        }
      }
    }
    return total;
  }


  getItemsForBar(localIndex: number, datasetIndex?: number) {
    const globalIndex = this.currentPage * this.pageSize + localIndex;
    const itemsInBucket = this.bucketItems[globalIndex] ?? [];
    if (datasetIndex == 0) {
      return itemsInBucket.filter(item => !item.is_part_of_work_plan);
    }
    if (datasetIndex == 1) {
      return itemsInBucket.filter(item => item.is_part_of_work_plan);
    }
    return itemsInBucket;
  }
}
